// Global
import React, { useMemo } from 'react';

// Local

import { CardListing as AdvancedCardListing } from '@generated/CardListing/AdvancedContentGrid.model';
import {
  ContentGrid,
  ContentGridCardAlignment,
  ContentGridViewType,
} from '../ContentGrid/ContentGrid';
import { ComponentParams, GetComponentServerProps, Item } from '@sitecore-content-sdk/nextjs';
import { usePreambleFields } from 'components/helpers/Molecules/Preamble/Preamble.sitecore';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { EditingHelpText } from 'components/helpers/Editing/EditingHelpText';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import { CardFields, fieldTypes } from '../CardListingShared';

// Data fetching
import {
  ContentListWithOverrides,
  DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
  getContentListWithOverrides,
} from 'lib/utils/components/content-list-data';
import { PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { ContentQueryProvider } from '../ContentGrid/ContentQueryContext';
import { RenderingParamBoolean } from 'lib/types/sitecore';
import { useFavoritesPage } from '../FavoritesPageContext';
import { FacetConfig, FacetConfigProvider } from '../ContentGrid/FacetConfigContext';
import { LayoutServiceContext, RouteData, LayoutServiceData } from '@sitecore-content-sdk/nextjs';
import graphqlClientFactory from 'lib/graphql-client-factory';
import { getSiteRoot } from 'lib/gql/getSiteRoot';
import { normalizeGuid } from 'lib/utils/string-utils';
import SearchConfigurationQuery, {
  FacetGroupQuery,
  FacetGroupQueryResult,
  SearchConfigurationQueryResult,
} from '../ContentGrid/SearchConfiguration.graphql';
import { Legacy } from '@generated/Foundation.GMI.model';
import { buildContentGridContentResultAnalyticsConfig } from 'components/helpers/Analytics/ContentGrid/ContentGridAnalytics';
import { resolveContentPageSize } from '../CardListingShared';
import { useDefaultCardDetailLevel } from 'lib/hooks/useSiteSettings';

export type AdvancedContentGridParams = ComponentParams & {
  cardDetails: {
    fieldsToShow: Item[];
  };
  gridAspectRatio: Item;
  cardView?: string;
  numColumns?: '1' | '2' | '3' | '4';
  centerLastRow?: RenderingParamBoolean;
  constrainedComponentWidth?: RenderingParamBoolean;
  showFacets?: RenderingParamBoolean;
  showSorting?: RenderingParamBoolean;
  showPagination?: RenderingParamBoolean;
  showResultCount?: RenderingParamBoolean;
};
type AdvancedContentGridProps = Omit<
  AdvancedCardListing.AdvancedContentGrid.AdvancedContentGrid_Component,
  'params'
> & {
  staticProps: ContentListWithOverrides | null;
  searchFacetConfig?: FacetConfig;
  params?: AdvancedContentGridParams;
  fields?: AdvancedCardListing.AdvancedContentGrid.AdvancedContentGrid_Component['fields'] & {
    sortByOptions?: Item[];
    sortByDirections?: Item[];
  };
  sitecoreContext: LayoutServiceContext;
  route: RouteData;
  layoutData?: LayoutServiceData;
};

const parseCheckboxValue = (value: unknown): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    return normalizedValue === '1' || normalizedValue === 'true' || normalizedValue === 'yes';
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  return false;
};

const getSearchConfigurationValue = (
  fields: Record<string, unknown> | undefined
): string | undefined => {
  const nextValue = (fields?.searchConfiguration as { value?: string } | undefined)?.value;
  if (nextValue) {
    return nextValue;
  }

  return (fields?.searchConfiguaration as { value?: string } | undefined)?.value;
};

const buildSearchConfigurationPath = ({
  value,
  siteRootPath,
}: {
  value: string;
  siteRootPath?: string;
}) => {
  const normalizedId = normalizeGuid(value);
  if (normalizedId) {
    return `{${normalizedId}}`;
  }

  if (value.startsWith('/sitecore/')) {
    return value;
  }

  if (!siteRootPath) {
    return undefined;
  }

  return `${siteRootPath}/Data/Search/Search Configurations/${value}`;
};

const mapFacetGroupToConfig = (
  facetGroup: NonNullable<FacetGroupQueryResult['item']>,
  overrideGroupLabel?: string
): FacetConfig['facetGroup'][number] | null => {
  const groupName = facetGroup.groupName?.jsonValue?.value ?? '';
  const groupLabel = overrideGroupLabel || facetGroup.groupLabel?.jsonValue?.value || groupName;
  if (!groupName || !groupLabel) {
    return null;
  }

  const expandedByDefault = facetGroup.expandedByDefault?.jsonValue?.value ?? false;
  const rawThreshold = facetGroup.showMoreThreshold?.jsonValue?.value as string | undefined;
  const parsedThreshold = rawThreshold ? parseInt(rawThreshold, 10) : NaN;
  const sortItemName = facetGroup.sortOrder?.jsonValue?.[0]?.name?.toLowerCase() ?? '';
  const sortOrder = sortItemName.includes('alpha')
    ? 'alphabetical'
    : sortItemName.includes('count')
      ? 'count'
      : undefined;

  const facets = (facetGroup.children?.results ?? [])
    .map((facet) => {
      const facetId = facet.facetId?.jsonValue?.value ?? '';
      const valueLabel = facet.valueLabel?.jsonValue?.value ?? '';
      if (!facetId || !valueLabel) {
        return null;
      }

      const isPinned = parseCheckboxValue(
        facet.isPinned?.jsonValue?.value ?? facet.isPinnedDisplayName?.jsonValue?.value
      );

      return {
        facetId,
        valueLabel,
        ...(isPinned ? { isPinned: true } : {}),
      };
    })
    .filter((facet): facet is { facetId: string; isPinned?: true; valueLabel: string } => !!facet);

  if (facets.length === 0) {
    return null;
  }

  return {
    groupName,
    groupLabel,
    facets,
    expandedByDefault,
    ...(sortOrder ? { sortOrder } : {}),
    ...(!Number.isNaN(parsedThreshold) ? { showMoreThreshold: parsedThreshold } : {}),
  };
};

const loadFacetConfig = async ({
  language,
  searchConfigurationValue,
  siteRootPath,
}: {
  language: string;
  searchConfigurationValue: string;
  siteRootPath?: string;
}) => {
  const path = buildSearchConfigurationPath({ value: searchConfigurationValue, siteRootPath });

  if (!path) {
    return { facetConfig: undefined };
  }

  const client = graphqlClientFactory();
  const { item } = await client.request<SearchConfigurationQueryResult>(SearchConfigurationQuery, {
    path,
    language,
  });

  if (!item) {
    return { facetConfig: undefined };
  }

  const facetGroupRefs = (item.facetGroup?.jsonValue ?? []).filter(
    (
      facetGroup
    ): facetGroup is {
      id?: string;
      name?: string;
    } => !!facetGroup
  );

  // "Facet Group Override Settings" child items let authors override just the
  // display label of a base Facet Group without duplicating its facet values.
  // Each override references a base Facet Group via `facetGroup` and supplies
  // a replacement `groupLabel`. `facetGroup`'s raw `value` is either a GUID
  // (Droplink reference) or plain text (Droplist selection); detect which via
  // `normalizeGuid` and key the override map accordingly.
  const overrideLabelByFacetGroupId = new Map<string, string>();
  const overrideLabelByFacetGroupName = new Map<string, string>();
  (item.children?.results ?? []).forEach((child) => {
    const fields = child.fields ?? [];
    const overrideLabel = fields.find((f) => f.name === 'groupLabel')?.value;
    const refValue = fields.find((f) => f.name === 'facetGroup')?.value;
    if (!overrideLabel || !refValue) {
      return;
    }

    const normalizedId = normalizeGuid(refValue);
    if (normalizedId) {
      overrideLabelByFacetGroupId.set(normalizedId, overrideLabel);
    } else {
      overrideLabelByFacetGroupName.set(refValue.trim().toLowerCase(), overrideLabel);
    }
  });
  // We intentionally ignore `item.facetGroup.targetItems` (the expanded shape
  // returned by `SearchConfigurationQuery`) and always refetch each facet
  // group individually via `FacetGroupQuery`. Reason: when the fragment that
  // selects `children` is applied through `MultilistField.targetItems`,
  // Sitecore Edge multiplies the per-child field cost by the number of
  // targets. Adding an explicit `first:` argument to `children` in that
  // fragment breaches the query-cost budget and the whole query is rejected
  // (facetConfig becomes undefined and no facets render). Per-ref queries
  // each get their own budget, so `children(first: 100)` in `FacetGroupQuery`
  // is safe and returns all facet values (not just the first 10).
  const facetGroups =
    facetGroupRefs.length > 0
      ? (
          await Promise.all(
            facetGroupRefs.map(async (facetGroupRef) => {
              if (!facetGroupRef.id) {
                return undefined;
              }

              const { item: facetGroup } = await client.request<FacetGroupQueryResult>(
                FacetGroupQuery,
                {
                  path: `{${normalizeGuid(facetGroupRef.id) ?? facetGroupRef.id}}`,
                  language,
                }
              );

              return facetGroup;
            })
          )
        ).filter(
          (facetGroup): facetGroup is NonNullable<FacetGroupQueryResult['item']> => !!facetGroup
        )
      : [];

  const mappedFacetGroups = facetGroups
    .map((facetGroup) => {
      const normalizedId = normalizeGuid(facetGroup.id) ?? facetGroup.id;
      const overrideLabel =
        overrideLabelByFacetGroupId.get(normalizedId) ??
        overrideLabelByFacetGroupName.get(facetGroup.name?.trim().toLowerCase() ?? '');
      return mapFacetGroupToConfig(facetGroup, overrideLabel);
    })
    .filter((facetGroup): facetGroup is FacetConfig['facetGroup'][number] => !!facetGroup);
  return {
    facetConfig: mappedFacetGroups.length > 0 ? { facetGroup: mappedFacetGroups } : undefined,
  };
};

const AdvancedContentGridContent = (props: AdvancedContentGridProps) => {
  const hasFavorites = useFavoritesPage();
  // Get the default card detail level from site settings (handles both flat and nested shapes)
  const defaultCardDetailLevel = useDefaultCardDetailLevel();

  // Priority: 1) cardDetails from component params, 2) defaultCardDetailLevel from site settings, 3) all fields
  const fieldsToShow = props.params?.cardDetails?.fieldsToShow?.length
    ? props.params.cardDetails.fieldsToShow.map((x: Item) => x.name as CardFields)
    : defaultCardDetailLevel?.length
      ? defaultCardDetailLevel.map((x) => x.name as CardFields)
      : [...fieldTypes];

  const isEditing = useIsEditing();
  const preambleFields = usePreambleFields({
    fields: props.fields,
    ctaComponentClassOverrides: {
      cta1: 'component-content-grid-color-button-color-1',
      cta2: 'component-content-grid-color-button-color-2',
    },
  });

  const gridAspectRatio = getEnumValue<ImageAspectRatio>(props.params?.gridAspectRatio);
  const viewType: ContentGridViewType =
    props.params?.cardView === 'ListView' ? 'ListView' : 'CardView';
  const contentListItem = props.fields?.contentList as
    | Legacy.ContentDataSources.CuratedContentDataSource_Item
    | Legacy.ContentDataSources.DynamicContentDataSource_Item
    | undefined;

  const isCuratedContentDataSource = !!(
    contentListItem as Legacy.ContentDataSources.CuratedContentDataSource_Item | undefined
  )?.fields?.Items;

  // Check for multiple AdvancedContentGrid components on the page
  const { hasMultipleInstances, componentCount } = useMemo(() => {
    const route = props.sitecoreContext?.route || props.layoutData?.sitecore?.route || props.route;
    const allAdvancedContentGridComponents: unknown[] = [];

    if (route) {
      type RouteComponent =
        | {
            componentName?: string;
            placeholders?: Record<string, unknown>;
          }
        | null
        | undefined;

      const findAllComponents = (
        item: RouteComponent,
        componentName: string,
        results: unknown[] = []
      ) => {
        if (!item) return results;

        // Check if current item is the component we're looking for
        if (item.componentName === componentName) {
          results.push(item);
        }

        // Recursively check placeholders
        if (item.placeholders) {
          Object.values(item.placeholders).forEach((placeholder) => {
            if (Array.isArray(placeholder)) {
              placeholder.forEach((component) =>
                findAllComponents(component, componentName, results)
              );
            }
          });
        }

        return results;
      };

      // Use the actual Sitecore component name, not the React function name
      findAllComponents(route, 'AdvancedContentGrid', allAdvancedContentGridComponents);
    }

    return {
      hasMultipleInstances: allAdvancedContentGridComponents.length > 1,
      componentCount: allAdvancedContentGridComponents.length,
    };
  }, [props.sitecoreContext?.route, props.layoutData?.sitecore?.route, props.route]);
  if (!props.staticProps?.result && !hasFavorites) {
    return null;
  }

  const resolvedSortByOptions =
    props.fields?.sortByOptions ??
    (typeof props.fields === 'object' &&
    props.fields &&
    'contentList' in props.fields &&
    typeof props.fields.contentList === 'object' &&
    props.fields.contentList &&
    'fields' in props.fields.contentList &&
    typeof props.fields.contentList.fields === 'object' &&
    props.fields.contentList.fields &&
    'sortByOptions' in props.fields.contentList.fields
      ? (props.fields.contentList.fields as { sortByOptions?: Item[] }).sortByOptions
      : undefined) ??
    undefined;
  const resolvedSortByDirections =
    props.fields?.sortByDirections ??
    (typeof props.fields === 'object' &&
    props.fields &&
    'contentList' in props.fields &&
    typeof props.fields.contentList === 'object' &&
    props.fields.contentList &&
    'fields' in props.fields.contentList &&
    typeof props.fields.contentList.fields === 'object' &&
    props.fields.contentList.fields &&
    'sortByDirections' in props.fields.contentList.fields
      ? (props.fields.contentList.fields as { sortByDirections?: Item[] }).sortByDirections
      : undefined) ??
    undefined;

  // Facets should only be enabled when a Search Configuration has been
  // selected on the AdvancedContentGrid component. If no configuration is
  // selected, we disable facets even if the rendering parameter is checked.
  const searchConfigurationValue = getSearchConfigurationValue(
    props.fields as Record<string, unknown> | undefined
  );
  const hasSearchConfiguration = !!searchConfigurationValue;
  const showFacets = hasSearchConfiguration && props.params?.showFacets === '1';
  const analyticsHeading = props.fields?.headline?.value?.trim() || undefined;
  const contentResultAnalytics = buildContentGridContentResultAnalyticsConfig(
    props.fields?.contentList,
    analyticsHeading
  );

  const result = (
    <FacetConfigProvider facetConfig={props.searchFacetConfig}>
      <ContentQueryProvider
        withFacets={showFacets}
        contentListItem={hasFavorites === true ? undefined : props.fields?.contentList}
        initialSearchResponse={
          hasFavorites === true ? null : props.staticProps ? props.staticProps.result : null
        }
        enableFavoritesAPI={hasFavorites === true}
        pageSize={resolveContentPageSize(props.staticProps?.result?.PageSize)}
      >
        {isEditing && hasMultipleInstances && (
          <EditingHelpText priority="warning">
            ⚠️ WARNING: Multiple Advanced Content Grid components detected on this page (
            {componentCount} instances). There should only be ONE Advanced Content Grid component
            per page. Please remove the extra instances.
          </EditingHelpText>
        )}
        {isEditing && isCuratedContentDataSource && props.params?.showSorting === '1' && (
          <EditingHelpText priority="warning">
            ⚠️ WARNING: if the sorting option is checked on curated content list, it will not be
            functional and will cause errors in the experience editor. Please uncheck the sorting
            option or add a Dynamic content source to the datasource.
          </EditingHelpText>
        )}
        <ContentGrid
          theme="ThemesPrimary"
          preambleOrientation={props.params?.preambleOrientation as PreambleOrientation}
          cardAlignment={props.params?.cardAlignment as ContentGridCardAlignment}
          gridAspectRatio={gridAspectRatio}
          viewType={viewType}
          {...preambleFields}
          contentOverrides={props.staticProps?.contentOverrides || {}}
          timedReleaseOverrides={props.staticProps?.timedReleaseOverrides || {}}
          fallbackImageUrl={props.staticProps?.fallbackImageUrl}
          fieldsToShow={fieldsToShow}
          columns={props.params?.numColumns || '3'}
          centerLastRow={props.params?.centerLastRow === '1'}
          constrainedComponentWidth={props.params?.constrainedComponentWidth === '1'}
          showPagination={props.params?.showPagination === '1'}
          showResultCount={props.params?.showResultCount === '1'}
          showSorting={props.params?.showSorting === '1'}
          showFacets={showFacets}
          sortByOptions={resolvedSortByOptions}
          sortByDirections={resolvedSortByDirections}
          contentResultAnalytics={contentResultAnalytics}
        />
      </ContentQueryProvider>
    </FacetConfigProvider>
  );

  return result;
};

export const Default = (props: AdvancedContentGridProps) => {
  return <AdvancedContentGridContent {...props} />;
};

export const getComponentServerProps: GetComponentServerProps = async (rendering, layoutData) => {
  const staticProps = await getContentListWithOverrides(rendering, layoutData, {
    withFacets: true,
    gridAspectRatio:
      getEnumValue<ImageAspectRatio>(
        rendering.params?.gridAspectRatio as unknown as Parameters<typeof getEnumValue>[0]
      ) ?? DEFAULT_CARD_FALLBACK_ASPECT_RATIO,
  });

  // Attempt to load the configured Search Configuration item and build
  // a facet configuration from it. If anything fails, we simply do not
  // provide facet configuration and the UI will not render facets.
  const componentSearchConfigValue = getSearchConfigurationValue(
    rendering.fields as Record<string, unknown> | undefined
  );

  const language = layoutData?.sitecore?.context?.language ?? 'en';
  const needsSiteScopedLookup = !!(
    componentSearchConfigValue &&
    !normalizeGuid(componentSearchConfigValue) &&
    !componentSearchConfigValue.startsWith('/sitecore/')
  );
  const siteName = layoutData?.sitecore?.context?.site?.name;
  const siteRoot =
    needsSiteScopedLookup && siteName
      ? await getSiteRoot(siteName, language).catch(() => undefined)
      : undefined;
  const siteRootPath = siteRoot?.path;

  let searchFacetConfig: FacetConfig | undefined;

  if (componentSearchConfigValue) {
    try {
      const { facetConfig } = await loadFacetConfig({
        searchConfigurationValue: componentSearchConfigValue,
        language,
        siteRootPath,
      });
      searchFacetConfig = facetConfig;
    } catch {}
  }

  // Avoid returning undefined in props (Next.js serialization restriction).
  // Only include searchFacetConfig when we have a value; otherwise omit it.
  const result = searchFacetConfig
    ? {
        staticProps,
        searchFacetConfig,
      }
    : {
        staticProps,
      };

  return result;
};
